/**
 * Media Permission Education Service
 * Handles educational content and messaging logic
 */

import { EDUCATION_MESSAGES, STORAGE_KEYS } from './permissionConstants'

/**
 * Get educational content based on media type
 */
export const getEducationContent = (mediaType = 'both') => {
  const contentKey =
    mediaType === 'both'
      ? 'VIDEO_CALL'
      : mediaType === 'camera'
        ? 'CAMERA_ONLY'
        : 'MICROPHONE_ONLY'

  return EDUCATION_MESSAGES[contentKey] || EDUCATION_MESSAGES.VIDEO_CALL
}

/**
 * Check if education has been shown before
 */
export const hasSeenEducation = (sessionType = 'general') => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.EDUCATION_SHOWN)
    if (!stored) return false

    const data = JSON.parse(stored)
    return data[sessionType] === true
  } catch (error) {
    console.error('Error reading education status:', error)
    return false
  }
}

/**
 * Mark education as shown
 */
export const markEducationShown = (sessionType = 'general') => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.EDUCATION_SHOWN)
    const data = stored ? JSON.parse(stored) : {}

    data[sessionType] = true
    data.lastShown = new Date().toISOString()

    localStorage.setItem(STORAGE_KEYS.EDUCATION_SHOWN, JSON.stringify(data))
  } catch (error) {
    console.error('Error saving education status:', error)
  }
}

/**
 * Reset education status (for testing or user request)
 */
export const resetEducationStatus = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.EDUCATION_SHOWN)
  } catch (error) {
    console.error('Error resetting education status:', error)
  }
}

/**
 * Get permission history from storage
 */
export const getPermissionHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PERMISSION_HISTORY)
    if (!stored) return []

    return JSON.parse(stored)
  } catch (error) {
    console.error('Error reading permission history:', error)
    return []
  }
}

/**
 * Add entry to permission history
 */
export const addPermissionHistoryEntry = (entry) => {
  try {
    const history = getPermissionHistory()
    
    const newEntry = {
      ...entry,
      timestamp: new Date().toISOString(),
    }

    history.push(newEntry)

    // Keep only last 50 entries
    const trimmedHistory = history.slice(-50)

    localStorage.setItem(STORAGE_KEYS.PERMISSION_HISTORY, JSON.stringify(trimmedHistory))
  } catch (error) {
    console.error('Error saving permission history:', error)
  }
}

/**
 * Get last permission check time
 */
export const getLastPermissionCheck = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.LAST_PERMISSION_CHECK)
    return stored ? new Date(stored) : null
  } catch (error) {
    console.error('Error reading last permission check:', error)
    return null
  }
}

/**
 * Update last permission check time
 */
export const updateLastPermissionCheck = () => {
  try {
    localStorage.setItem(STORAGE_KEYS.LAST_PERMISSION_CHECK, new Date().toISOString())
  } catch (error) {
    console.error('Error saving last permission check:', error)
  }
}

/**
 * Determine if we should show education based on history
 */
export const shouldShowEducation = (sessionType = 'general', forceShow = false) => {
  if (forceShow) return true

  // As per plan: "First visit: Full education flow every time"
  // So we always show education
  return true
}

/**
 * Get permission denial count for analytics
 */
export const getPermissionDenialCount = () => {
  const history = getPermissionHistory()
  return history.filter((entry) => entry.result === 'denied').length
}

/**
 * Check if user has persistent permission issues
 */
export const hasPersistentPermissionIssues = () => {
  const history = getPermissionHistory()
  
  // Get last 5 attempts
  const recentHistory = history.slice(-5)
  
  // If 3 or more of last 5 attempts were denials, consider it persistent
  const denialCount = recentHistory.filter((entry) => entry.result === 'denied').length
  
  return denialCount >= 3
}

/**
 * Get personalized message based on user history
 */
export const getPersonalizedMessage = () => {
  const history = getPermissionHistory()
  
  if (history.length === 0) {
    return {
      tone: 'welcoming',
      message: 'Welcome! To get started with your video call, we need access to your camera and microphone.',
    }
  }

  const lastEntry = history[history.length - 1]
  
  if (lastEntry.result === 'denied') {
    return {
      tone: 'helpful',
      message: 'We noticed you had trouble with permissions before. Let us help you get set up properly this time.',
    }
  }

  if (lastEntry.result === 'success') {
    return {
      tone: 'friendly',
      message: 'Welcome back! Ready to join your video call?',
    }
  }

  return {
    tone: 'neutral',
    message: 'To join the video call, please allow access to your camera and microphone.',
  }
}

/**
 * Get tips based on user's browser and history
 */
export const getContextualTips = (browser, deviceType) => {
  const tips = []

  // Browser-specific tips
  if (browser === 'safari') {
    tips.push('Safari users: Make sure to click "Allow" when prompted')
  }

  if (browser === 'firefox') {
    tips.push('Firefox users: Permission prompts appear in the address bar')
  }

  // Device-specific tips
  if (deviceType === 'mobile') {
    tips.push('Mobile users: Check your system settings if the prompt doesn\'t appear')
  }

  // History-based tips
  if (hasPersistentPermissionIssues()) {
    tips.push('Having trouble? Our support team is ready to help')
  }

  return tips
}

/**
 * Export all education data (for debugging)
 */
export const exportEducationData = () => {
  return {
    hasSeenEducation: hasSeenEducation(),
    permissionHistory: getPermissionHistory(),
    lastCheck: getLastPermissionCheck(),
    denialCount: getPermissionDenialCount(),
    hasPersistentIssues: hasPersistentPermissionIssues(),
  }
}

/**
 * Clear all education data
 */
export const clearAllEducationData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.EDUCATION_SHOWN)
    localStorage.removeItem(STORAGE_KEYS.PERMISSION_HISTORY)
    localStorage.removeItem(STORAGE_KEYS.LAST_PERMISSION_CHECK)
  } catch (error) {
    console.error('Error clearing education data:', error)
  }
}


















